import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Paper, Tabs, Tab, Box, Typography } from '@mui/material';
import axios from 'axios';
import SlabInputForm from './SlabInputForm';
import SlabResults from './SlabResults';
import CalculationHistory from '../shared/CalculationHistory';

const API_BASE = '/api/slab-calculator';
const SAVED_API = `${API_BASE}/saved-analyses`;

function buildSlabInputData(input) {
  if (!input) return {};
  return {
    L_m: input.L_m,
    W_m: input.W_m,
    slab_type: input.slab_type ?? 'KL150',
    slab_family: input.slab_family ?? 'kuorilaatta',
    h_top_mm: input.h_top_mm,
    n_tendons: input.n_tendons,
    diameter_tendon_mm: input.diameter_tendon_mm,
    tension_mpa: input.tension_mpa,
    tendon_c_nom_mm: input.tendon_c_nom_mm,
    top_slab_cover_bottom_mm: input.top_slab_cover_bottom_mm,
    rebar_x: input.rebar_x,
    rebar_y: input.rebar_y,
    point_loads_var: input.point_loads_var,
    superimposed_dead_load_kN_m2: -(Math.abs(input.sdl_display ?? input.superimposed_dead_load_kN_m2 ?? 0)),
    uniform_live_load_kN_m2: input.uniform_live_load_kN_m2 ?? 0,
    mesh_density: input.mesh_density,
    edge_conditions: input.edge_conditions,
    beam_supports: input.beam_supports,
    bearing_k_per_m: input.bearing_k_per_m,
    bearing_capacity_per_m: input.bearing_capacity_per_m,
    interior_support_y_m: input.interior_support_y_m?.length ? input.interior_support_y_m : null,
    wq_composite_mode: input.wq_composite_mode ?? false,
    sheet_thickness_mm: input.sheet_thickness_mm ?? null,
    longitudinal_shear_resistance_kN_m: input.longitudinal_shear_resistance_kN_m ?? null,
    wq_beam: input.wq_beam ?? null,
    t0_days: input.t0_days,
    t_service_days: input.t_service_days,
    rh_pct: input.rh_pct,
    temp_c: input.temp_c ?? 20,
    tendon_preset: input.tendon_preset,
    include_images: !!input.include_images,
  };
}

const SlabAnalysis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const historyRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loadedInputData, setLoadedInputData] = useState(null);
  const [inputsChangedSinceAnalysis, setInputsChangedSinceAnalysis] = useState(false);
  const formDataRef = useRef(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getSlabSavePayload = useCallback(() => {
    const input = formDataRef?.current;
    const input_data = buildSlabInputData(input);
    return {
      name: input ? `Slab ${input.L_m}x${input.W_m}m` : 'Slab case',
      input_data,
      results_data: analysisResults || {},
    };
  }, [analysisResults]);

  const handleAnalysisComplete = (results) => {
    setAnalysisResults(results);
    setInputsChangedSinceAnalysis(false);
    setActiveTab(1);
    const input = formDataRef?.current;
    historyRef.current?.saveAuto?.({
      input_data: buildSlabInputData(input),
      results_data: results || {},
    });
  };

  const handleInputChange = () => {
    if (analysisResults) setInputsChangedSinceAnalysis(true);
  };

  useEffect(() => {
    const sid = searchParams.get('calcSession');
    if (!sid) return;
    let cancelled = false;
    axios
      .get(`${SAVED_API}/${sid}/`)
      .then((r) => {
        if (cancelled) return;
        const { input_data, results_data } = r.data;
        if (input_data) setLoadedInputData(input_data);
        if (results_data && Object.keys(results_data).length) {
          setAnalysisResults(results_data);
          setActiveTab(1);
        } else {
          setActiveTab(0);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, '@media print': { display: 'none' } }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flex: 1 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Slab Input" />
              <Tab label="Analysis Results" disabled={!analysisResults} />
            </Tabs>
          </Box>
          <CalculationHistory
            ref={historyRef}
            apiEndpoint={SAVED_API}
            getSavePayload={getSlabSavePayload}
            appLabel="Slab analysis"
            onLoad={({ input_data, results_data }) => {
              if (input_data) setLoadedInputData(input_data);
              setInputsChangedSinceAnalysis(false);
              if (results_data && Object.keys(results_data).length) {
                setAnalysisResults(results_data);
                setActiveTab(1);
              } else {
                setAnalysisResults(null);
                setActiveTab(0);
              }
            }}
            renderSummary={(item) => (
              <Typography variant="caption" color="text.secondary" component="span">
                {item.input_data?.L_m != null && item.input_data?.W_m != null
                  ? `${item.input_data.L_m}×${item.input_data.W_m} m`
                  : '—'}
              </Typography>
            )}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
            <SlabInputForm
              onAnalysisComplete={handleAnalysisComplete}
              formDataRef={formDataRef}
              initialData={loadedInputData}
              onInitialDataApplied={() => setLoadedInputData(null)}
              onInputChange={handleInputChange}
            />
          </Box>

          <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
            {analysisResults && (
              <SlabResults
                results={analysisResults}
                inputsChangedSinceAnalysis={inputsChangedSinceAnalysis}
              />
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SlabAnalysis;
